import { AMERIA_PREFIX, PHONE_REGEX } from "@/shared/common/constants";
import type {
  ICommission,
  IError,
  IExtendedObject,
  IFormatAmountProps,
  IObject
} from "@/shared/common/types";

export const buildRoute = (route: string, params: Record<string, string | number>) => {
  return route?.replace(/:([a-zA-Z]+)/g, (_, key) => encodeURIComponent(params[key] + ""));
};

export const getTotalCommission = (commissionData: ICommission) => {
  return commissionData ?
    Number(commissionData?.commission) + Number(commissionData?.additionalCommission) : 0;
};

export const getNameChars = (name: string) => {
  let chars = "";
  if (!name) return;
  const trimmed = name.trimStart().trimEnd();

  if (trimmed?.includes(" ")) {
    const words = trimmed.split(" ");
    chars = words[0][0] + words[words.length - 1][0];
  } else {
    chars = trimmed[0] + trimmed[1];
  }
  return chars;
};

export const trimAll = (value: string) => {
  return value?.replace(/\s+/g, "");
};

export const isExistInEnum = (checkingEnum: IObject, key: string) => {
  return Object.values(checkingEnum).includes(key);
};

export const isEqual = (a: IExtendedObject | unknown, b: IExtendedObject | unknown) => {
  if (a === b) return true;

  if (a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime();
  }

  if (a instanceof Date || b instanceof Date) return false;

  if (
    typeof a !== "object" ||
    typeof b !== "object" ||
    a === null ||
    b === null
  ) {
    return false;
  }

  const keysA = Object.keys(a);
  const keysB = Object.keys(b);

  if (keysA.length !== keysB.length) return false;

  for (const key of keysA) {
    if (!keysB.includes(key)) return false;
    const objA = a as Record<string, unknown>;
    const objB = b as Record<string, unknown>;
    if (!isEqual(objA[key], objB[key])) return false;
  }

  return true;
};

export const getErrorMessage = (error: IError, fallback?: string) => {
  return error?.data?.warnings?.[0] || fallback || "";
};

export const isValidPhoneNumber = (phone: string) => {
  if (!phone) return true;
  return PHONE_REGEX.test(phone);
};

export const formatAmount = ({ amount, currency }: IFormatAmountProps) => {
  if (amount === undefined || amount === null || amount === "") return "";

  const sanitized = `${amount}`.replace(/[^\d.-]/g, "");
  const number = Number(sanitized);

  if (isNaN(number)) return "";

  return `${new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(number)} ${currency}`;
};

export const formatPhoneNumber = (phone: string) => {
  if (!phone) return "";

  const clean = phone.replace(/[^\d+]/g, "");

  const hasPlus = clean.startsWith("+");
  const digits = hasPlus ? clean.slice(1) : clean;

  let formatted = "";

  if (digits.length <= 4) {
    formatted = digits;
  } else if (digits.length <= 7) {
    formatted = digits.replace(/(\d{3})(\d+)/, "$1 $2");
  } else if (digits.length <= 10) {
    formatted = digits.replace(/(\d{3})(\d{3})(\d+)/, "$1 $2 $3");
  } else {
    formatted = digits.replace(/(\d{1,3})(\d{3})(\d{3})(\d+)/, "$1 $2 $3 $4");
  }

  return hasPlus ? `+${formatted}` : formatted;
};

export const removeAllSpacesAndDashes = (value: string) =>
  value.replace(/[\s\-]/g, "");

export const htmlToBlobUrl = (html: string): {
  url: string
  revoke: () => void
} => {
  const blob = new Blob([html], { type: "text/html" });
  const url = URL.createObjectURL(blob);

  return {
    url,
    revoke: () => URL.revokeObjectURL(url)
  };
};

export const isAmeriaAccount = (account?: string) => {
  return account?.startsWith(AMERIA_PREFIX);
};

export const omitKey = <T extends object, K extends keyof T>(
  obj: T,
  keys: K | K[]
): Omit<T, K> => {
  const keysToRemove = Array.isArray(keys) ? keys : [keys];
  const result = { ...obj };
  keysToRemove.forEach(key => delete result[key]);
  return result as Omit<T, K>;
};

export const mergeByKeys = <T extends Record<string, unknown>>(
  target: T,
  source: Partial<T>
) => {
  const output: T = { ...target };

  (Object.keys(source) as Array<keyof T>).forEach((key) => {
    const value = source[key];
    if (value !== undefined) {
      output[key] = value as T[typeof key];
    }
  });

  return output;
};
